/**
 * GST Verification Badge Component
 * 
 * Displays color-coded status indicator for GST verification with tooltip
 * 
 * Requirements: 4.6, 15.2
 * Task: 25.1
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import { GSTVerificationStatus } from '../../types/lead';

interface GSTVerificationBadgeProps {
  status: GSTVerificationStatus;
  verifiedAt?: string | null;
  size?: 'small' | 'medium';
  iconOnly?: boolean; // New prop for icon-only display
}

/**
 * Badge component for displaying GST verification status
 * 
 * Color coding:
 * - Verified: Green with checkmark icon
 * - Unverified: Red with X icon
 * - Pending: Yellow with clock icon
 * - Not Applicable: Gray with dash icon
 * 
 * Includes tooltip with verification timestamp when available
 */
const GSTVerificationBadge: React.FC<GSTVerificationBadgeProps> = ({
  status,
  verifiedAt,
  size = 'small',
  iconOnly = false,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case GSTVerificationStatus.VERIFIED:
        return {
          color: 'success' as const,
          icon: <CheckCircleIcon />,
          label: 'GST Verified',
        };
      case GSTVerificationStatus.UNVERIFIED:
        return {
          color: 'error' as const,
          icon: <CancelIcon />,
          label: 'GST Unverified',
        };
      case GSTVerificationStatus.PENDING:
        return {
          color: 'warning' as const,
          icon: <ScheduleIcon />,
          label: 'GST Pending',
        };
      case GSTVerificationStatus.NOT_APPLICABLE:
        return {
          color: 'default' as const,
          icon: <RemoveCircleIcon />,
          label: 'No GST',
        };
      default:
        return {
          color: 'default' as const,
          icon: <RemoveCircleIcon />,
          label: 'Unknown',
        };
    }
  };

  const config = getStatusConfig();

  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const getTooltipTitle = (): string => {
    if (status === GSTVerificationStatus.NOT_APPLICABLE) {
      return 'No GST number provided';
    }
    
    if (verifiedAt) {
      const formattedDate = formatTimestamp(verifiedAt);
      return `${config.label} on ${formattedDate}`;
    }
    
    return config.label;
  };

  // Icon-only display for inline use
  if (iconOnly) {
    return (
      <Tooltip title={getTooltipTitle()} arrow placement="top">
        {React.cloneElement(config.icon, {
          sx: {
            fontSize: '1rem',
            color: 
              config.color === 'success' ? 'success.main' :
              config.color === 'error' ? 'error.main' :
              config.color === 'warning' ? 'warning.main' :
              'text.secondary',
          },
        })}
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTooltipTitle()} arrow placement="top">
      <Chip
        label={config.label}
        color={config.color}
        icon={config.icon}
        size={size}
        variant="outlined"
        sx={{
          borderRadius: 4,
          fontWeight: 500,
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? '0.875rem' : '1rem',
          },
        }}
      />
    </Tooltip>
  );
};

export default GSTVerificationBadge;
